import { EventBus } from "./event_bus.js";
import { MessageBus } from "./message_bus.js";
import { Retry } from "./retry.js";
import { Utils } from "./utils.js";
import { APP } from "./app.js";

// Who is recording: the playtester signed in on the platform, on this browser, if any. It comes only from the platform's
// sign-in cookie, asked once per page load, and stays in this page's memory. A guest flow is always the fallback: no
// answer, an older server or a blocked cookie change nothing. Lines on existing screens, never a modal.
// docs/proposals/2026-10-08-recognise-signed-in-playtester.md
export class PlaytesterIdentity {
  // Set on leaving for the platform, read once on coming back. Not who: only that they went.
  static RETURN_MARKER = "playcocola-sign-in-return";
  static LEAVING_WAIT_MS = 500; // Navigating cancels the debug event of the click
  static PLACES = ["card", "steps", "thoughts"];

  state = "checking" // recognised | not_signed_in | mismatch | unavailable | guest
  isUnlinking = false

  #answer = {}
  #openPlace = null
  #refused = false
  #checked
  #resolveChecked
  #slots = {}

  constructor() {
    this.#checked = new Promise((resolve) => { this.#resolveChecked = resolve; });

    PlaytesterIdentity.PLACES.forEach((place) => {
      this.#slots[place] = this.#buildSlot(place);
    });
  }

  // API :: INI
  get isRecognised() {
    return this.state === "recognised";
  }

  get recordingsUrl() {
    return this.isRecognised ? this.#answer.recordingsUrl : null;
  }

  // Resolves once the first answer is in, whatever it was
  whenChecked() {
    return this.#checked;
  }

  async check() {
    const returning = this.#takeReturnMarker();

    if (!APP.recordingManager.isBrowserSupported || !APP.playcocolaAPI.isPlatformApi()) {
      MessageBus.debug("Recognition :: skipped");
      this.#settle("unavailable");
      return;
    }

    try {
      this.#applyAnswer(await APP.playcocolaAPI.requestRecognition(), returning);
    } catch (error) {
      this.#failed(Utils.normalizeError(error).message);
    }
  }
  // API :: END

  #applyAnswer(answer, returning) {
    const api = APP.playcocolaAPI;

    switch (answer?.status) {
      case "recognised":
        this.#answer = {
          displayName: answer.display_name ?? null,
          guestToken: answer.guest_token ?? null,
          switchAccountUrl: api.platformUrl(answer.switch_account_url),
          recordingsUrl: api.platformUrl(answer.recordings_url)
        };
        MessageBus.debug("Recognition :: recognised");
        if (returning) {
          MessageBus.debug("Recognition :: return recognised");
        }
        this.#settle("recognised");
        break;

      case "not_signed_in":
        // Back from signing in and still unknown: the browser blocks the cookie, and another invitation would only loop
        if (returning) {
          this.#failed("back from the platform, not recognised");
          break;
        }
        this.#answer = { signInUrl: api.platformUrl(answer.sign_in_url), signUpUrl: api.platformUrl(answer.sign_up_url) };
        MessageBus.debug("Recognition :: not signed in");
        this.#settle("not_signed_in");
        break;

      case "bounty_mismatch":
        this.#answer = { switchAccountUrl: api.platformUrl(answer.switch_account_url) };
        MessageBus.debug("Recognition :: bounty mismatch");
        this.#settle("mismatch");
        break;

      default:
        this.#failed("unknown answer");
    }
  }

  #failed(reason) {
    MessageBus.debug(`Recognition :: failed: ${reason}`);
    this.#settle("unavailable");
  }

  #settle(state) {
    this.#changeState(state);
    this.#resolveChecked();
  }

  #changeState(state) {
    this.state = state;
    this.#render();
    EventBus.event("playtester-identity-changed");
  }

  // Nothing changes on the page until the platform confirms it
  async #recordAsGuest() {
    if (this.isUnlinking) {
      return;
    }

    this.isUnlinking = true;
    this.#refused = false;
    this.#render();
    EventBus.event("playtester-identity-changed");
    MessageBus.debug("Recognition :: record as guest");

    try {
      await APP.playcocolaAPI.requestRecordAsGuest(this.#answer.guestToken);
      this.isUnlinking = false;
      this.#openPlace = null;
      this.#answer = {};
      MessageBus.debug("Recognition :: record as guest confirmed");
      this.#changeState("guest");
    } catch (error) {
      this.isUnlinking = false;
      this.#refused = true;
      MessageBus.debug(`Recognition :: record as guest refused: ${Utils.normalizeError(error).message}`);
      this.#render();
      EventBus.event("playtester-identity-changed");
    }
  }

  // The same tab: the platform brings them back to this link, and the page loads fresh
  #leaveFor(url, message) {
    this.#setReturnMarker();

    Promise.race([MessageBus.debug(message), Retry.wait(PlaytesterIdentity.LEAVING_WAIT_MS)]).then(() => {
      window.location.assign(url);
    });
  }

  #setReturnMarker() {
    try {
      window.sessionStorage.setItem(PlaytesterIdentity.RETURN_MARKER, "1");
    } catch (error) {
      console.warn("PlaytesterIdentity: no sessionStorage", error);
    }
  }

  #takeReturnMarker() {
    try {
      const returning = window.sessionStorage.getItem(PlaytesterIdentity.RETURN_MARKER) !== null;
      window.sessionStorage.removeItem(PlaytesterIdentity.RETURN_MARKER);
      return returning;
    } catch (error) {
      return false;
    }
  }

  #buildSlot(place) {
    const root = document.querySelector(`#playtester-identity-${place}`);
    root.append(document.querySelector("#playtester-identity-template").content.cloneNode(true));

    const slot = {
      root,
      icon: root.querySelector("[data-identity-icon]"),
      text: root.querySelector("[data-identity-text]"),
      note: root.querySelector("[data-identity-note]"),
      notYou: root.querySelector("[data-identity-not-you]"),
      actions: root.querySelector("[data-identity-actions]"),
      recordAsGuest: root.querySelector("[data-identity-record-as-guest]"),
      switchAccount: root.querySelector("[data-identity-switch-account]"),
      links: root.querySelector("[data-identity-links]"),
      signIn: root.querySelector("[data-identity-sign-in]"),
      signUp: root.querySelector("[data-identity-sign-up]"),
      refused: root.querySelector("[data-identity-refused]")
    };

    slot.notYou.addEventListener("click", () => {
      this.#openPlace = this.#openPlace === place ? null : place;
      this.#render();
    });

    slot.recordAsGuest.addEventListener("click", () => { this.#recordAsGuest(); });

    slot.switchAccount.addEventListener("click", () => {
      this.#leaveFor(this.#answer.switchAccountUrl, "Recognition :: switch account clicked");
    });

    slot.signIn.addEventListener("click", (event) => {
      event.preventDefault();
      this.#leaveFor(this.#answer.signInUrl, "Recognition :: sign in clicked");
    });

    slot.signUp.addEventListener("click", (event) => {
      event.preventDefault();
      this.#leaveFor(this.#answer.signUpUrl, "Recognition :: sign up clicked");
    });

    return slot;
  }

  #render() {
    PlaytesterIdentity.PLACES.forEach((place) => this.#renderSlot(place, this.#slots[place]));
  }

  #renderSlot(place, slot) {
    const { text, note } = this.#wording(place);
    const canGoGuest = this.isRecognised && Boolean(this.#answer.guestToken);
    const canSwitch = place === "card" && Boolean(this.#answer.switchAccountUrl);
    const canInvite = place === "card" && this.state === "not_signed_in" && Boolean(this.#answer.signInUrl);
    const isOpen = this.state === "mismatch" || this.#openPlace === place;

    slot.root.dataset.state = this.state;
    slot.root.classList.toggle("d-none", text === "");
    slot.icon.classList.toggle("d-none", !this.isRecognised);
    slot.text.textContent = text;
    slot.note.textContent = note;
    slot.note.classList.toggle("d-none", note === "");

    slot.notYou.classList.toggle("d-none", !this.isRecognised || !(canGoGuest || canSwitch));
    slot.actions.classList.toggle("d-none", !isOpen || !(canGoGuest || canSwitch));
    slot.recordAsGuest.classList.toggle("d-none", !canGoGuest);
    slot.recordAsGuest.disabled = this.isUnlinking;
    slot.recordAsGuest.textContent = this.isUnlinking ? "Changing to guest…" : "Record as guest";
    slot.switchAccount.classList.toggle("d-none", !canSwitch);
    slot.switchAccount.disabled = this.isUnlinking;

    slot.links.classList.toggle("d-none", !canInvite);
    slot.signUp.classList.toggle("d-none", !this.#answer.signUpUrl);
    if (canInvite) {
      slot.signIn.href = this.#answer.signInUrl;
      slot.signUp.href = this.#answer.signUpUrl ?? "";
    }

    slot.refused.classList.toggle("d-none", !this.#refused || !this.isRecognised);
    slot.refused.textContent =
      `This Play Session can't leave your account from here. Please write to ${APP.playcocolaAPI?.playtestRound?.supportEmail ?? "hello@playcocola.com"}.`;
  }

  // All wording is provisional (see the proposal). The name goes in as text.
  #wording(place) {
    const name = this.#answer.displayName;

    if (this.isRecognised) {
      return {
        card: {
          text: name ? `Welcome back, ${name}.` : "Welcome back.",
          note: "This Play Session will be on your Playcocola profile, and the developer will see your profile, email included."
        },
        steps: {
          text: name ? `Recording as ${name}.` : "Recording with your Playcocola account.",
          note: "It goes on your Playcocola profile, and the developer sees your profile, email included."
        },
        thoughts: { text: name ? `Sending as ${name}.` : "Sending with your Playcocola account.", note: "" }
      }[place];
    }

    if (this.state === "mismatch" && place !== "thoughts") {
      return { text: "This Bounty Invitation belongs to another playtester account.", note: "" };
    }

    if (this.state === "not_signed_in" && place === "card" && this.#answer.signInUrl) {
      return { text: "Have a Playcocola playtester account? Sign in so this Play Session is added to your profile.", note: "" };
    }

    return { text: "", note: "" };
  }
}
